import * as React from "react";
import { Alert, Box } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import LoadingScreen from "../progress/loading-screen";
import { WATCH_LIST_SEARCH_RESULTS_LOADING_MESSAGES } from "../search-results/constants";
import { WatchListSearchResult } from "./types";
import { getEntityColumns } from "./data-grid";
import AddWatchlistDialog from "./add-dialog";

const WatchListEntities = ({
  entities,
  showResults,
  isLoading,
  error,
}: {
  entities: WatchListSearchResult[] | undefined;
  showResults: boolean;
  isLoading: boolean;
  error: Error | boolean | null;
}) => {
  const [selectedElement, setSelectedElement] =
    React.useState<WatchListSearchResult | null>(null);

  const handleOnAdd = React.useCallback((element: WatchListSearchResult) => {
    setSelectedElement(element);
  }, []);

  if (isLoading) {
    return (
      <LoadingScreen
        height={300}
        dataTestId="search-results-loading"
        loadingMessages={WATCH_LIST_SEARCH_RESULTS_LOADING_MESSAGES}
      />
    );
  }
  if (error) {
    return <Alert severity="error">Unable to load watch list entities</Alert>;
  }

  if (!entities || entities.length === 0 || !showResults) {
    return (
      <Box
        width="100%"
        textAlign="center"
        marginTop={12}
        color="text.secondary"
      >
        {showResults ? (
          "No results found for the given search criteria."
        ) : (
          <>
            Your search results will show up here. <br />
            Please enter search criteria.
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      textAlign="center"
      marginTop={2}
      color="text.secondary"
      sx={{ backgroundColor: "background.paper", borderRadius: 1 }}
    >
      <DataGrid
        rows={
          entities.map((entity) => ({ ...entity, id: entity.iBaseId })) || []
        }
        columns={getEntityColumns({
          onAdd: handleOnAdd,
        })}
        disableRowSelectionOnClick
        loading={isLoading}
        sx={{
          border: "none",
          height: "560px",
          [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
            outline: "transparent",
          },
          [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: "none",
            },
          [`& .${gridClasses.row}:hover`]: {
            cursor: "pointer",
          },
          "& .row-disabled": {
            opacity: 0.5,
            pointerEvents: "none",
          },
        }}
        slotProps={{
          loadingOverlay: {
            variant: "circular-progress",
            noRowsVariant: "circular-progress",
          },
          baseIconButton: {
            size: "small",
          },
        }}
      />

      {selectedElement && (
        <AddWatchlistDialog
          selectedEntity={selectedElement}
          open={selectedElement !== null}
          onClose={() => setSelectedElement(null)}
        />
      )}
    </Box>
  );
};

export default WatchListEntities;
